import { Link } from "react-router-dom";
import { MapPin, Clock, Accessibility, Video, Car, Shirt, Info, CalendarPlus, Navigation, Download, Users, Phone, Globe } from "lucide-react";
import { L, mediaUrl, useFetch, useTitle } from "../lib";
import { PageHeader } from "../ui";

const ROOT = `${process.env.REACT_APP_BACKEND_URL}/api/louise`;

const Line = ({ icon: I, label, children, testid }) => children ? (
  <div className="flex gap-3 text-sm leading-relaxed" data-testid={testid}>
    <I size={17} className="text-lou-clay shrink-0 mt-0.5" aria-hidden />
    <p className="whitespace-pre-line">{label && <span className="text-lou-soft">{label}. </span>}{children}</p>
  </div>
) : null;

const osmEmbed = (lat, lon) =>
  `https://www.openstreetmap.org/export/embed.html?bbox=${lon - 0.006},${lat - 0.0035},${lon + 0.006},${lat + 0.0035}&layer=mapnik&marker=${lat},${lon}`;

// Same gathering on several days (e.g. a two-evening wake) is shown as one card
export const groupEvents = (events) => events.reduce((out, e) => {
  const key = [e.name, e.venue, e.address, e.start_time, e.end_time].join("|");
  const g = out.find((x) => x.key === key);
  if (g) g.days.push(e); else out.push({ ...e, key, days: [e] });
  return out;
}, []);

const shortDay = (d) => d.display_date?.replace(/, \d{4}$/, "");

function EventCard({ e }) {
  const id = e.id.slice(0, 8);
  const multi = e.days.length > 1;
  return (
    <section className="border border-lou-line border-l-4 border-l-lou-sage bg-lou-paper/90" data-testid={`arr-event-${id}`}>
      <div className="p-6 sm:p-10 grid md:grid-cols-5 gap-8">
        <div className="md:col-span-2">
          {e.kind && <p className="text-xs tracking-[0.2em] uppercase text-lou-clay">{e.kind}</p>}
          <h2 className="font-lou text-3xl font-light mt-2" data-testid={`arr-event-name-${id}`}>{e.name}</h2>
          {e.days.map((d, i) => d.display_date && <p key={d.id} className={`font-lou text-xl ${i ? "mt-1" : "mt-4"}`} data-testid={`arr-event-date-${d.id.slice(0, 8)}`}>{d.display_date}</p>)}
          {e.display_time && <p className="text-lou-soft mt-2" data-testid={`arr-event-time-${id}`}>{multi ? `Both evenings, ${e.display_time}` : e.display_time}</p>}
          {e.tz_label && <p className="text-xs text-lou-soft mt-1" data-testid={`arr-event-tz-${id}`}>{e.tz_label}</p>}
          <div className="flex flex-wrap gap-3 mt-6">
            {e.days.map((d) => d.can_calendar && <a key={d.id} href={`${ROOT}/arrangements/events/${d.id}/calendar.ics`} className="lou-btn-ghost text-sm" data-testid={`arr-calendar-${d.id.slice(0, 8)}`}><CalendarPlus size={16} /> {multi ? `Add ${shortDay(d).split(",")[0]}` : "Add to Calendar"}</a>)}
            {e.directions_url && <a href={e.directions_url} target="_blank" rel="noreferrer" className="lou-btn-ghost text-sm" data-testid={`arr-directions-${id}`}><Navigation size={16} /> Open Directions</a>}
          </div>
        </div>
        <div className="md:col-span-3 space-y-4">
          <Line icon={MapPin} testid={`arr-event-venue-${id}`}>{[e.venue, e.address].filter(Boolean).join("\n")}</Line>
          <Line icon={Info} label="Directions">{e.directions}</Line>
          <Line icon={Car} label="Parking">{e.parking}</Line>
          <Line icon={Accessibility} label="Accessibility">{e.accessibility}</Line>
          <Line icon={Shirt} label="Dress">{e.dress}</Line>
          <Line icon={Clock}>{e.instructions}</Line>
          <Line icon={Users} label="RSVP">{e.rsvp}</Line>
          {e.livestream_url && <a href={e.livestream_url} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-sm hover:text-lou-clay" data-testid={`arr-livestream-${id}`}><Video size={17} className="text-lou-clay" /> Watch the livestream</a>}
          {e.phone && <a href={`tel:${e.phone.replace(/[^\d+]/g, "")}`} className="flex items-center gap-3 text-sm hover:text-lou-clay" data-testid={`arr-phone-${id}`}><Phone size={17} className="text-lou-clay" /> {e.phone}</a>}
          {e.website && <a href={e.website} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-sm hover:text-lou-clay break-all" data-testid={`arr-website-${id}`}><Globe size={17} className="text-lou-clay shrink-0" /> {e.website.replace(/^https?:\/\/(www\.)?/, "").split("/")[0]}</a>}
        </div>
      </div>
      {e.can_map && (
        <div className="border-t border-lou-line">
          <iframe title={`Map of ${e.venue || e.address}`} src={osmEmbed(e.lat, e.lon)} className="w-full h-72 block" loading="lazy" data-testid={`arr-map-${id}`} />
          <p className="text-[11px] text-lou-soft px-4 py-2">Map © OpenStreetMap contributors</p>
        </div>
      )}
    </section>
  );
}

const linkify = (t) => t.split(/(https?:\/\/[^\s]+?)(?=\.?(?:\s|$))/).map((p, i) =>
  /^https?:\/\//.test(p) ? <a key={i} href={p} target="_blank" rel="noreferrer" className="text-lou-clay underline underline-offset-4">{p.replace(/^https?:\/\//, "").replace(/\/$/, "")}</a> : p);

function Obituary({ o }) {
  return (
    <section className="mt-20 pt-14 border-t border-lou-line" data-testid="arr-obituary">
      <p className="text-xs tracking-[0.28em] uppercase text-lou-clay">Obituary</p>
      <h2 className="font-lou text-3xl sm:text-4xl font-light mt-3" data-testid="arr-obituary-title">{o.title}</h2>
      {o.dates && <p className="font-lou italic text-xl text-lou-soft mt-2" data-testid="arr-obituary-dates">{o.dates}</p>}
      <div className="mt-10 grid md:grid-cols-12 gap-10 items-start">
        {o.photo_media_id && (
          <figure className="md:col-span-4">
            <img src={mediaUrl(o.photo_media_id)} alt="Louise M. Gatanas" className="w-full max-w-[280px] shadow-sm" data-testid="arr-obituary-photo" />
          </figure>
        )}
        <div className={o.photo_media_id ? "md:col-span-8" : "md:col-span-12"}>
          <div className="space-y-5 text-[17px] leading-relaxed max-w-2xl" data-testid="arr-obituary-text">
            {o.text.split("\n\n").map((p, i) => <p key={i}>{linkify(p)}</p>)}
          </div>
          {o.pdf_media_id && (
            <a href={`${mediaUrl(o.pdf_media_id)}?download=1`} className="lou-btn-ghost inline-flex mt-10 text-sm" data-testid="arr-obituary-download">
              <Download size={16} /> Download Obituary (PDF)
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

function Donations({ items }) {
  return (
    <section className="mt-16 border border-lou-line border-l-4 border-l-lou-clay bg-lou-paper/90 p-8 sm:p-10" data-testid="arr-donations">
      <p className="text-xs tracking-[0.28em] uppercase text-lou-clay">In Louise’s memory</p>
      <h2 className="font-lou text-3xl font-light mt-3">Donations</h2>
      <p className="text-lou-soft mt-3 max-w-2xl">Donations in Louise’s memory can be made to any of the following.</p>
      <ul className="mt-8 grid sm:grid-cols-3 gap-4">
        {items.map((d, i) => (
          <li key={d.name}>
            <a href={d.url} target="_blank" rel="noreferrer" data-testid={`arr-donation-${i}`}
              className="group flex h-full flex-col justify-between gap-6 border border-lou-line bg-white/70 p-5 transition-colors hover:border-lou-clay">
              <span className="font-lou text-xl leading-snug">{d.name}</span>
              <span className="inline-flex items-center gap-2 text-sm text-lou-clay">Donate <Globe size={14} className="transition-transform group-hover:translate-x-0.5" /></span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Invitation() {
  return (
    <section className="mt-20 px-6 py-16 text-center lou-wash-rose" data-testid="arr-invitation">
      <p className="font-lou font-light italic text-2xl sm:text-3xl leading-snug">If you would like to share a story, a photograph, or a few words in Louise’s memory, we would love to hear from you.</p>
      <div className="flex flex-wrap justify-center gap-4 mt-10">
        <Link to={L("/share")} className="lou-btn" data-testid="arr-share-memory">Share a Memory of Louise</Link>
        <Link to={L("/remembering")} className="lou-btn-ghost" data-testid="arr-read-memories">Read Memories of Louise</Link>
      </div>
    </section>
  );
}

const updated = (iso) => new Date(iso).toLocaleString("en-US", { month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/New_York", timeZoneName: "short" });

export default function Arrangements() {
  const { data } = useFetch("/arrangements");
  useTitle("Funeral and Memorial Arrangements");
  if (!data) return <div className="h-[70vh]" />;
  const has = data.events.length > 0;
  return (
    <div data-testid="arrangements-page">
      <PageHeader eyebrow="In Louise's memory" title="Funeral and Memorial Arrangements" intro={has ? data.intro : ""} />
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="max-w-5xl">
        {!has ? (
          <p className="font-lou text-2xl font-light leading-relaxed border-l-2 border-lou-clay/50 pl-6 max-w-2xl" data-testid="arr-pending">{data.pending_message}</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              {data.updated_at && <p className="text-xs text-lou-soft" data-testid="arr-updated">Last updated {updated(data.updated_at)}</p>}
              {data.can_download && <a href={`${ROOT}/arrangements/download.pdf`} className="lou-btn text-sm" data-testid="arr-download"><Download size={16} /> Download Arrangements</a>}
            </div>
            <div className="space-y-8">{groupEvents(data.events).map((e) => <EventCard key={e.id} e={e} />)}</div>
            {data.notes && <p className="text-lou-soft leading-relaxed whitespace-pre-line mt-10" data-testid="arr-notes">{data.notes}</p>}
          </>
        )}
        {data.obituary?.text && <Obituary o={data.obituary} />}
        {data.donations?.length > 0 && <Donations items={data.donations} />}
        <Invitation />
        </div>
      </div>
    </div>
  );
}
